
import React from 'react';
import { WhatsAppButton } from './WhatsAppButton';
import { ScrollAnimation } from './ScrollAnimation';
import { PianoIcon } from './icons/PianoIcon';
import { InstagramIcon } from './icons/InstagramIcon';
import { YoutubeIcon } from './icons/YoutubeIcon';
import { DynamicBackground } from './DynamicBackground';
import { useTranslation } from '../i18n/LanguageContext';
import { customSmoothScroll, getScrollTargetPosition } from '../utils/scroll';

const YOUTUBE_CHANNEL_URL = "https://www.youtube.com/channel/UC6RMlexCZ2i26RZ9oo8xdDw?view_as=subscriber";
const INSTAGRAM_URL = "https://www.instagram.com/nikolasyuri7/";


export const CallToActionFooter: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const footerNavLinks = t('navbar.links') as { href: string, text: string }[];

  const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    event.preventDefault();
    const targetId = href.startsWith('#') ? href.substring(1) : null;
    if (!targetId) return;

    const targetY = getScrollTargetPosition(targetId);
    customSmoothScroll(targetY, 2000); // 2 segundos de scroll
    
    try {
      if (window.history.pushState) {
        window.history.pushState(null, "", href);
      }
    } catch (error) {}
  };


  return (
    <footer id="contato" className="bg-brand-secondary text-brand-text pt-16 md:pt-24 relative isolate overflow-hidden">
      <DynamicBackground />
      <div className="relative z-10">
        {/* Bloco de Chamada Final */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center pb-16 md:pb-24">
          <ScrollAnimation animation="slideInUp">
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-brand-light mb-6"
            >
              {t('footer.finalCta.title')}
            </h2>
            <p className="text-lg text-brand-text max-w-2xl mx-auto mb-8">
              {t('footer.finalCta.subtitle')}
            </p>
          </ScrollAnimation>
          <ScrollAnimation animation="slideInUp" delay={150}>
            <WhatsAppButton 
              text={t('footer.finalCta.button')}
              className="px-8 py-4 text-base sm:text-lg font-semibold
                         text-white
                         border-none 
                         rounded-lg shadow-xl
                         transform hover:scale-105
                         focus:ring-2 focus:ring-offset-2 focus:ring-offset-brand-primary focus:ring-gradient-end
                         transition-all duration-300 ease-in-out"
              ariaLabel={t('footer.finalCta.ariaLabel')}
            />
          </ScrollAnimation>
        </div>
        
        <ScrollAnimation animation="fadeIn" delay={300}>
          <div className="border-t border-slate-700 py-12 bg-brand-primary/50">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
                
                <div className="flex flex-col items-center md:items-start">
                  <div className="flex items-center gap-3 mb-3">
                     <PianoIcon className="w-8 h-8 text-brand-accent" />
                     <span className="text-xl font-serif font-bold text-brand-light">{t('brandName')}</span>
                  </div>
                  <p className="text-sm text-slate-400 mb-3 max-w-xs">
                    {t('footer.tagline')}
                  </p>
                  <p className="text-xs text-slate-500">&copy; {currentYear} {t('instructorName')}. {t('footer.copyright')}</p>
                </div>

                <div className="md:text-center">
                  <h4 className="font-semibold text-brand-light mb-4 tracking-wider uppercase text-sm">{t('footer.navigate')}</h4>
                  <ul className="space-y-2">
                    {footerNavLinks.map(link => (
                       <li key={link.href}>
                          <a 
                            href={link.href} 
                            onClick={(e) => handleLinkClick(e, link.href)}
                            className="text-brand-text hover:text-brand-accent transition-colors text-sm"
                          >
                            {link.text}
                          </a>
                       </li>
                    ))}
                  </ul>
                </div>

                <div className="md:text-center">
                  <h4 className="font-semibold text-brand-light mb-4 tracking-wider uppercase text-sm">{t('footer.follow')}</h4>
                  <div className="flex justify-center md:justify-center space-x-6">
                    <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label={`${t('brandName')} on Instagram`} className="text-brand-text hover:text-brand-accent transition-colors">
                      <InstagramIcon className="w-7 h-7" />
                    </a>
                    <a href={YOUTUBE_CHANNEL_URL} target="_blank" rel="noopener noreferrer" aria-label={`${t('brandName')} on YouTube`} className="text-brand-text hover:text-brand-accent transition-colors">
                      <YoutubeIcon className="w-7 h-7" />
                    </a>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </ScrollAnimation>
      </div>
    </footer>
  );
};
